import { useTranslation } from 'react-i18next'

import type { ScreenOpenVote } from '../types'
import { CompactCandidateCard, ScreenCountdown, gridFor } from './shared'

/**
 * Le vote ouvert, à la télé : pour qui l'on vote, et combien de temps reste.
 *
 * Les nommés en photo, avec leur numéro — celui du bulletin sur le téléphone.
 * Aucun bouton : la télé ne vote pas, elle montre. Aucun compteur de voix non
 * plus : la salle ne doit pas voter pour celui qui mène.
 *
 * Deux votes ouverts ensemble (Miss et Master) : deux colonnes, chacune avec
 * son décompte.
 */
export function VoteBoards({
  boards,
  serverTime,
}: {
  boards: ScreenOpenVote[]
  serverTime: string
}) {
  const { t } = useTranslation()
  const single = boards.length === 1

  return (
    <div className="flex h-full min-h-0 flex-col gap-[2vh]">
      <div className="flex items-end justify-between gap-[3vw]">
        <div className="min-w-0">
          <p className="font-display text-[min(7vh,4.2vw)] leading-none text-ember-300">
            {t('screen.voteOpen')}
          </p>
          <p className="mt-[1.2vh] text-[min(3vh,1.8vw)] text-content-secondary">
            {t('screen.voteHow')}
          </p>
        </div>
        {single ? (
          <ScreenCountdown endsAt={boards[0]!.countdown_ends_at} serverTime={serverTime} />
        ) : null}
      </div>

      <div
        className="grid min-h-0 flex-1 gap-[2.5vw]"
        style={{ gridTemplateColumns: `repeat(${boards.length}, minmax(0, 1fr))` }}
      >
        {boards.map((board) => (
          <VoteBoard
            key={board.vote_category_id}
            board={board}
            columns={boards.length}
            serverTime={serverTime}
          />
        ))}
      </div>
    </div>
  )
}

function VoteBoard({
  board,
  columns,
  serverTime,
}: {
  board: ScreenOpenVote
  columns: number
  serverTime: string
}) {
  const { cols, rows } = gridFor(board.nominees.length, columns)
  const title = board.round ?? board.title

  return (
    <section className="flex min-h-0 min-w-0 flex-col gap-[1.5vh]">
      {/* Le concours en surtitre, le tour en dessous : en deux colonnes, une
          seule ligne « Miss · Tenue traditionnelle » serait coupée. */}
      <div className="flex items-end justify-between gap-[2vh]">
        <div className="min-w-0">
          {board.contest ? (
            <p className="text-[min(2.8vh,1.7vw)] uppercase tracking-[0.18em] text-ember-300">
              {board.contest}
            </p>
          ) : null}
          <h2 className="truncate font-display text-[min(4.6vh,2.7vw)] leading-tight text-champagne-300">
            {title}
          </h2>
        </div>
        {columns > 1 ? (
          <ScreenCountdown endsAt={board.countdown_ends_at} serverTime={serverTime} compact />
        ) : null}
      </div>
      <ul
        className="grid min-h-0 flex-1 gap-[1.6vh]"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
      >
        {board.nominees.map((nominee) => (
          <li key={nominee.id} className="flex min-h-0 min-w-0">
            <CompactCandidateCard
              name={nominee.name}
              number={nominee.number}
              photoUrl={nominee.photo_url}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}
